/**
 * Vendor detail model — adapts DB-backed vendor + bill rows to the
 * `prototype/purchases/vendor-detail.html` layout.
 *
 * No prototype ledger data lives here. Values come from `vendors.get` and
 * `bills.list({ vendorId })`; unavailable verticals render explicit
 * placeholders instead of inferred financial facts.
 */
import type { vendors as vendorContract } from "@flint/api-contract";
import type { z } from "zod";
import { paiseToInrShort } from "../../../lib/format.ts";
import type { VendorDetailMetrics } from "../components/detail-stat-strip.tsx";
import { panFromGstin, vendorSince } from "./format.ts";

export type VendorDetailRow = z.infer<typeof vendorContract.vendorRow>;
export interface VendorBillWireRow {
  id: string;
  vendorId: string;
  vendorName: string;
  vendorDocNumber: string | null;
  receivedOn: string;
  dueOn: string | null;
  totalPaise: string;
  status: "draft" | "review" | "approval_pending" | "ready_to_pay" | "paid" | "void";
  threeWayMatch: "matched" | "mismatch" | "na";
  source: "photo" | "email_pdf" | "manual";
  createdAt: string;
}

export type VendorBillTone = "awaiting" | "review" | "paid" | "draft" | "void";
export type VendorTimelineTone = "await" | "match" | "issued" | "paid" | "note";

export interface VendorBillRow {
  id: string;
  displayNumber: string;
  date: string;
  description: string;
  amount: string;
  statusLabel: string;
  tone: VendorBillTone;
  meta: string;
}

export interface VendorTimelineItem {
  id: string;
  tone: VendorTimelineTone;
  title: string;
  when: string;
  body: string;
  linkLabel?: string;
}

export interface VendorTimelineGroup {
  label: string;
  items: VendorTimelineItem[];
}

export interface VendorDetailModel {
  summary: string;
  subtitle: string;
  pills: Array<{ label: string; tone: "warn" | "ok" | "sky" | "muted" }>;
  metrics: VendorDetailMetrics;
  pan: string | null;
  taxMode: string;
  contactRole: string;
  billsSummary: string;
  bills: VendorBillRow[];
  awaitingBill: {
    id: string;
    amount: string;
    meta: string;
  } | null;
  activity: VendorTimelineGroup[];
  reliability: {
    percent: string;
    rows: Array<{ label: string; value: string; tone?: "ok" }>;
    dots: Array<"on" | "now" | "empty">;
  };
  spendTrend: {
    months: Array<{ label: string; tone?: "now"; heightClass: string }>;
    ytd: string;
    last12Months: string;
  };
  bank: {
    accountName: string;
    bankName: string;
    maskedAccount: string;
    ifsc: string;
    verification: string;
  };
  pinnedNote: {
    when: string;
    body: string;
  } | null;
  billCount: number;
  poCount: number;
  paymentCount: number;
  documentCount: number;
}

export function buildVendorDetailModel(
  vendor: VendorDetailRow,
  billsResult?: { items: VendorBillWireRow[] },
  /** Our workspace's home state — drives intra-vs-inter-state tax mode. */
  companyState = "",
): VendorDetailModel {
  const billRows = billsResult?.items ?? [];
  const activeBills = billRows.filter((bill) => bill.status !== "void");
  const payableBills = activeBills.filter((bill) =>
    ["review", "approval_pending", "ready_to_pay"].includes(bill.status),
  );
  const paidBills = activeBills.filter((bill) => bill.status === "paid");
  const ytdBills = activeBills.filter((bill) => isCurrentYear(bill.receivedOn));
  const payablePaise = sumPaise(payableBills);
  const ytdPaise = sumPaise(ytdBills);
  const last12Paise = sumPaise(
    activeBills.filter((bill) => isWithinLastMonths(bill.receivedOn, 12)),
  );
  const awaiting = payableBills[0] ?? null;
  const pan = panFromGstin(vendor.gstin);
  const terms = normalizeTerms(vendor.paymentTerms);
  const city = vendor.city || "City not set";
  const taxMode =
    companyState && vendor.state === companyState
      ? "Intra-state · CGST + SGST"
      : "Inter-state · IGST";

  return {
    summary: `${activeBills.length} bill${activeBills.length === 1 ? "" : "s"} on file · vendor since ${vendorSince(vendor.createdAt)}`,
    subtitle: `${city}-based supplier · ${terms} · TDS ${vendor.tdsSection || "not set"}`,
    pills: buildPills(vendor, activeBills, payableBills, payablePaise),
    metrics: {
      owe: {
        value: payablePaise > 0n ? paiseToInrShort(payablePaise) : "₹0",
        meta: payableBills.length
          ? `${payableBills.length} bill${payableBills.length === 1 ? "" : "s"} awaiting action`
          : "No unpaid bills from DB",
      },
      ytdSpend: {
        value: ytdPaise > 0n ? paiseToInrShort(ytdPaise) : "₹0",
        meta: `${ytdBills.length} bill${ytdBills.length === 1 ? "" : "s"} recorded this year`,
      },
      avgPayment: {
        value: paidBills.length ? "Paid" : "—",
        meta: paidBills.length
          ? `${paidBills.length} paid bill${paidBills.length === 1 ? "" : "s"}; payment dates pending`
          : "Vendor payments not connected yet",
        placeholder: paidBills.length === 0,
      },
      reliability: {
        value: "—",
        meta: "GRN reliability feed not connected yet",
        placeholder: true,
      },
    },
    pan,
    taxMode,
    contactRole: vendor.contactName ? "account manager" : "contact not set",
    billsSummary: `${activeBills.length} bill${activeBills.length === 1 ? "" : "s"} · ${paiseToInrShort(ytdPaise)} spent this year`,
    bills: activeBills.map(toBillRow),
    awaitingBill: awaiting
      ? {
          id: billNumber(awaiting),
          amount: paiseToInrShort(awaiting.totalPaise),
          meta: awaitingMeta(awaiting),
        }
      : null,
    activity: buildActivity(vendor, activeBills),
    reliability: {
      percent: "—",
      rows: [
        { label: "GRNs connected", value: "Not yet" },
        { label: "Quality returns", value: "Not tracked" },
        { label: "Avg lead time", value: "PO feed pending" },
        { label: "Last deliveries", value: "No GRN rows" },
      ],
      dots: ["empty", "empty", "empty", "empty", "empty", "empty"],
    },
    spendTrend: {
      months: buildSpendMonths(activeBills),
      ytd: paiseToInrShort(ytdPaise),
      last12Months: paiseToInrShort(last12Paise),
    },
    bank: {
      accountName: vendor.name,
      bankName: vendor.hasBankDetails ? "Bank details on file" : "Not on file",
      maskedAccount: vendor.hasBankDetails
        ? "Masked by step-up policy"
        : "Add through founder step-up",
      ifsc: vendor.hasBankDetails ? "Step-up required" : "Not on file",
      verification: vendor.hasBankDetails
        ? "Encrypted details available after step-up"
        : "Add bank details before releasing payments",
    },
    pinnedNote: null,
    billCount: activeBills.length,
    poCount: 0,
    paymentCount: paidBills.length,
    documentCount: 0,
  };
}

function buildPills(
  vendor: VendorDetailRow,
  activeBills: VendorBillWireRow[],
  payableBills: VendorBillWireRow[],
  payablePaise: bigint,
): VendorDetailModel["pills"] {
  const pills: VendorDetailModel["pills"] = [];
  if (payableBills.length > 0) {
    pills.push({ label: `Awaiting · ${paiseToInrShort(payablePaise)}`, tone: "warn" });
  }
  if (vendor.gstin) pills.push({ label: "GSTIN verified", tone: "ok" });
  if (vendor.isJobWorker) pills.push({ label: "Job-worker · ITC-04", tone: "sky" });
  pills.push({ label: `${activeBills.length} bills`, tone: "sky" });
  const latest = activeBills[0];
  if (latest)
    pills.push({ label: `Last bill · ${formatShortDate(latest.receivedOn)}`, tone: "muted" });
  return pills;
}

function toBillRow(bill: VendorBillWireRow): VendorBillRow {
  const status = billStatus(bill.status);
  return {
    id: bill.id,
    displayNumber: billNumber(bill),
    date: formatShortDate(bill.receivedOn),
    description: `${sourceLabel(bill.source)} · ${bill.threeWayMatch === "matched" ? "3-way matched" : matchLabel(bill.threeWayMatch)}`,
    amount: paiseToInrShort(bill.totalPaise),
    statusLabel: status.label,
    tone: status.tone,
    meta: bill.dueOn ? `due ${formatShortDate(bill.dueOn)}` : "no due date",
  };
}

function buildActivity(vendor: VendorDetailRow, bills: VendorBillWireRow[]): VendorTimelineGroup[] {
  const billItems = bills.slice(0, 8).map((bill): VendorTimelineItem => {
    const status = billStatus(bill.status);
    return {
      id: bill.id,
      tone: status.timelineTone,
      title: `${billNumber(bill)} · ${status.activity}`,
      when: formatShortDate(bill.receivedOn),
      body: `${paiseToInrShort(bill.totalPaise)} · ${matchLabel(bill.threeWayMatch)} · ${sourceLabel(bill.source)}${bill.dueOn ? ` · due ${formatShortDate(bill.dueOn)}` : ""}`,
      linkLabel: "Open bills",
    };
  });

  return [
    ...(billItems.length ? [{ label: "Bill activity", items: billItems }] : []),
    {
      label: "Vendor master",
      items: [
        {
          id: `${vendor.id}-created`,
          tone: "issued",
          title: "Vendor added",
          when: formatDate(vendor.createdAt),
          body: `${vendor.gstin ? "GSTIN on file" : "GSTIN not provided"} · ${normalizeTerms(vendor.paymentTerms)} terms · ${vendor.state || "state not set"}.`,
        },
      ],
    },
  ];
}

function buildSpendMonths(bills: VendorBillWireRow[]): VendorDetailModel["spendTrend"]["months"] {
  const months = lastSixMonths();
  const totals = months.map((month) =>
    sumPaise(
      bills.filter((bill) => {
        const d = new Date(`${bill.receivedOn}T00:00:00`);
        return d.getFullYear() === month.year && d.getMonth() === month.monthIndex;
      }),
    ),
  );
  const max = totals.reduce((acc, value) => (value > acc ? value : acc), 0n);
  return months.map((month, index) => ({
    label: month.label,
    ...(index === months.length - 1 ? { tone: "now" as const } : {}),
    heightClass: heightClass(totals[index] ?? 0n, max),
  }));
}

function billStatus(status: VendorBillWireRow["status"]): {
  label: string;
  tone: VendorBillTone;
  activity: string;
  timelineTone: VendorTimelineTone;
} {
  switch (status) {
    case "approval_pending":
      return {
        label: "Awaiting OTP",
        tone: "awaiting",
        activity: "awaiting founder approval",
        timelineTone: "await",
      };
    case "ready_to_pay":
      return { label: "Ready", tone: "awaiting", activity: "ready to pay", timelineTone: "await" };
    case "review":
      return { label: "Review", tone: "review", activity: "needs review", timelineTone: "note" };
    case "paid":
      return { label: "Paid", tone: "paid", activity: "paid", timelineTone: "paid" };
    case "void":
      return { label: "Void", tone: "void", activity: "voided", timelineTone: "note" };
    case "draft":
    default:
      return { label: "Draft", tone: "draft", activity: "draft recorded", timelineTone: "issued" };
  }
}

function awaitingMeta(bill: VendorBillWireRow): string {
  const match = matchLabel(bill.threeWayMatch);
  const due = bill.dueOn ? `due ${formatShortDate(bill.dueOn)}` : "no due date";
  return `${match} · ${sourceLabel(bill.source)} · ${due}`;
}

function billNumber(bill: VendorBillWireRow): string {
  return bill.vendorDocNumber || `BILL-${bill.id.slice(0, 8).toUpperCase()}`;
}

function matchLabel(value: VendorBillWireRow["threeWayMatch"]): string {
  switch (value) {
    case "matched":
      return "3-way matched";
    case "mismatch":
      return "match mismatch";
    case "na":
    default:
      return "3-way match not applicable";
  }
}

function sourceLabel(value: VendorBillWireRow["source"]): string {
  switch (value) {
    case "email_pdf":
      return "Email PDF";
    case "photo":
      return "Photo";
    case "manual":
    default:
      return "Manual entry";
  }
}

function normalizeTerms(terms: string | null): string {
  if (!terms) return "Net 15";
  if (/^\d+\s*days$/i.test(terms)) return `Net ${terms.replace(/\D/g, "")}`;
  if (/^immediate$/i.test(terms)) return "Due on receipt";
  return terms;
}

function sumPaise(rows: VendorBillWireRow[]): bigint {
  return rows.reduce((sum, row) => sum + BigInt(row.totalPaise), 0n);
}

function isCurrentYear(isoDate: string): boolean {
  return new Date(`${isoDate}T00:00:00`).getFullYear() === new Date().getFullYear();
}

function isWithinLastMonths(isoDate: string, months: number): boolean {
  const d = new Date(`${isoDate}T00:00:00`);
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  return d >= cutoff;
}

function lastSixMonths(): Array<{ label: string; year: number; monthIndex: number }> {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return {
      label: d.toLocaleDateString("en-IN", { month: "short" }),
      year: d.getFullYear(),
      monthIndex: d.getMonth(),
    };
  });
}

function heightClass(value: bigint, max: bigint): string {
  if (value === 0n || max === 0n) return "h-3";
  const bucket = Number((value * 5n) / max);
  if (bucket >= 5) return "h-24";
  if (bucket >= 4) return "h-20";
  if (bucket >= 3) return "h-16";
  if (bucket >= 2) return "h-12";
  return "h-6";
}

function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
